import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { handleInput, handleSubmit, transformUnderscoreToCamel } from "../../../../services/utils/processors";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Input from "../../../../components/form/input";
import InputArea from "../../../../components/form/inputArea";
import styled from "styled-components";
import { GridRow, ActionRow } from "../form.styles";
import { useReplaceResume } from "../../../../services/api/users-api";
import { ToastContext } from "../../../../services/contexts/toast-context";

const ProfileWrapper = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SectionCard = styled.div`
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 8px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionLabel = styled.p`
  font-weight: 600;
  font-size: 0.95rem;
  margin: 0 0 4px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
  color: var(--text-color);
  text-align: left;
`;

const ResumeCard = styled.div`
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  background: var(--surface-card, transparent);

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ResumeCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  strong { color: var(--text-color); }
  span { color: var(--text-color-secondary); line-height: 1.5; }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const LinkRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  width: 100%;
  @media (max-width: 768px) { flex-direction: column; align-items: stretch; }
`;

const LinkPlatform = styled.div`
  flex: 1;
`;

const LinkUrl = styled.div`
  flex: 2;
`;

const editableFields = ['phone', 'bio', 'country', 'links'];

const linksToObject = (links) => Object.fromEntries(
  (Array.isArray(links) ? links : [])
    .filter(({ title, url }) => title?.trim() !== '' && url?.trim() !== '')
    .map(({ title, url }) => [title.trim(), url.trim()])
);

const ProfileForm = ({ dbData = null, loading = false, onSave, editMode = false }) => {
  const [info, setInfo] = useState({ links: [] });
  const [initialInfo, setInitialInfo] = useState({ links: [] });
  const [infoError, setInfoError] = useState({});
  const [resumeFile, setResumeFile] = useState(null);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const toastRef = React.useContext(ToastContext);
  const replaceResume = useReplaceResume();
  const resumeInputId = "replace-resume-upload";
  const navigate = useNavigate();
  const sourceKey = JSON.stringify(dbData || {});

  useEffect(() => {
    const transformed = transformUnderscoreToCamel(dbData || {});
    const linksArray = transformed.links && typeof transformed.links === 'object' && !Array.isArray(transformed.links)
      ? Object.entries(transformed.links).map(([title, url]) => ({ title, url }))
      : Array.isArray(transformed.links) ? transformed.links : [];
    const profileInfo = { ...transformed, links: linksArray };

    setInfo(profileInfo);
    setInitialInfo(JSON.parse(JSON.stringify(profileInfo)));
    setInfoError({});
  }, [dbData, sourceKey]);

  const isDirty = JSON.stringify(info) !== JSON.stringify(initialInfo);

  const schema = Yup.object({
    email: Yup.string().email("Invalid email format"),
    phone: Yup.string().matches(/\+?[1-9]\d{1,14}$/, "Invalid phone number").required("Phone number is required"),
    bio: Yup.string().required("Bio is required"),
    country: Yup.string().required("Country is required"),
    links: Yup.array().of(
      Yup.object({
        title: Yup.string().required("Platform is required"),
        url: Yup.string().required("URL is required"),
      })
    ),
  });

  const handleSubmitData = () => {
    handleSubmit(info, schema, setInfoError, (data) => {
      const currentData = { ...data, links: linksToObject(info.links) };
      const originalData = { ...initialInfo, links: linksToObject(initialInfo.links) };
      const changedData = Object.fromEntries(
        editableFields
          .filter((field) => JSON.stringify(currentData[field]) !== JSON.stringify(originalData[field]))
          .map((field) => [field, currentData[field]])
      );

      if (Object.keys(changedData).length > 0) {
        onSave(changedData);
      }
    });
  };

  const handleLinkChange = (index, field, value) => {
    setInfo((prev) => {
      const links = [...prev.links];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, links };
    });
  };

  const addLink = () => setInfo((prev) => ({ ...prev, links: [...prev.links, { title: '', url: '' }] }));
  const removeLink = (index) => setInfo((prev) => ({ ...prev, links: prev.links.filter((_, i) => i !== index) }));

  const handleResumeSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toastRef.current?.show({ severity: 'error', summary: 'Invalid file', detail: 'Please select a PDF resume.', life: 4000 });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toastRef.current?.show({ severity: 'error', summary: 'File too large', detail: 'The resume must be smaller than 5 MB.', life: 4000 });
      return;
    }
    setResumeFile(file);
    setShowResumeConfirm(true);
  };

  const handleResumeReplacement = () => {
    replaceResume.mutate(resumeFile, {
      onSuccess: () => {
        setResumeFile(null);
        setShowResumeConfirm(false);
        toastRef.current?.show({ severity: 'success', summary: 'Portfolio replaced', detail: 'Your profile was replaced with the information from the new resume.', life: 5000 });
      },
      onError: (error) => {
        toastRef.current?.show({ severity: 'error', summary: 'Replacement failed', detail: error.response?.data?.detail || 'Unable to replace your portfolio.', life: 5000 });
      },
    });
  };

  return (
    <ProfileWrapper>
      <SectionCard>
        <SectionLabel>Personal Information</SectionLabel>
        <GridRow>
          <Input disabled value={info.username || ''} label="Full Name" width="100%"
            onChange={(val) => handleInput(val, "username", setInfo, setInfoError, schema)}
            error={infoError.username} />
          <Input disabled value={info.email || ''} label="Email" type="email" width="100%"
            onChange={(val) => handleInput(val, "email", setInfo, setInfoError, schema)}
            error={infoError.email} />
        </GridRow>
        <GridRow>
          <Input value={info.phone || ''} label="Phone" width="100%"
            onChange={(val) => handleInput(val, "phone", setInfo, setInfoError, schema)}
            error={infoError.phone} />
          <Input value={info.country || ''} label="Country" width="100%"
            onChange={(val) => handleInput(val, "country", setInfo, setInfoError, schema)}
            error={infoError.country} />
        </GridRow>
        <InputArea value={info.bio || ''} label="Bio" width="100%" height="120px"
          onChange={(val) => handleInput(val, "bio", setInfo, setInfoError, schema)}
          error={infoError.bio} />
      </SectionCard>

      {editMode && (
        <ResumeCard>
          <ResumeCopy>
            <strong>Replace resume</strong>
            <span>Upload a new PDF to replace the resume attached to your profile. Your portfolio data will stay unchanged.</span>
          </ResumeCopy>
          <label htmlFor={resumeInputId}>
            <Button as="span" type="button" icon="pi pi-upload" label="Upload new resume" />
            <HiddenFileInput id={resumeInputId} type="file" accept="application/pdf,.pdf" onChange={handleResumeSelected} />
          </label>
        </ResumeCard>
      )}

      <SectionCard>
        <SectionLabel>Links & Socials</SectionLabel>
        {info.links.map((link, index) => (
          <LinkRow key={index}>
            <LinkPlatform>
              <Input value={link.title} label="Platform" width="100%"
                onChange={(val) => handleLinkChange(index, "title", val)}
                error={infoError.links?.[index]?.title} />
            </LinkPlatform>
            <LinkUrl>
              <Input value={link.url} label="URL" width="100%"
                onChange={(val) => handleLinkChange(index, "url", val)}
                error={infoError.links?.[index]?.url} />
            </LinkUrl>
            <Button type="button" icon="pi pi-trash" severity="danger" text onClick={() => removeLink(index)} />
          </LinkRow>
        ))}
        <Button type="button" icon="pi pi-plus" label="Add Link" text onClick={addLink} />
      </SectionCard>

      <ActionRow>
        {!editMode && <Button label="Back" severity="secondary" onClick={() => navigate("/onboarding/choose-profile")} />}
        {(!editMode || isDirty) && <Button loading={loading} label="Save" severity="primary" onClick={handleSubmitData} />}
      </ActionRow>

      <Dialog
        visible={showResumeConfirm}
        onHide={() => !replaceResume.isPending && setShowResumeConfirm(false)}
        header="Replace resume?"
        modal
        closable={!replaceResume.isPending}
        style={{ width: "min(500px, 90vw)" }}
      >
        <p>
          Uploading <strong>{resumeFile?.name}</strong> will replace your current resume file. Your projects, experience, education, certifications, and skills will not be changed.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
          <Button label="Cancel" text disabled={replaceResume.isPending} onClick={() => setShowResumeConfirm(false)} />
          <Button label="Replace resume" severity="primary" loading={replaceResume.isPending} onClick={handleResumeReplacement} />
        </div>
      </Dialog>
    </ProfileWrapper>
  );
};

export default ProfileForm;

